import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import LanguageSwitcher from '../common/LanguageSwitcher';
import StarRating from '../common/StarRating';
import './JobPage.css';

const getApiBaseUrl = () => {
  const envUrl = process.env.REACT_APP_API_URL;
  if (!envUrl) {
    return process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8081';
  }
  if (envUrl.includes('your-cloud-run-url') || envUrl.includes('placeholder')) {
    return process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8081';
  }
  if (envUrl.includes('run.app')) {
    return envUrl;
  }
  if (process.env.NODE_ENV === 'production') {
    return envUrl;
  }
  return 'http://localhost:8081';
};

const API_BASE_URL = getApiBaseUrl();

export default function JobPage() {
  const { conversationId } = useParams();
  const { currentUser, currentRole, logout } = useAuth();
  const { t, isRTL } = useLanguage();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [conversation, setConversation] = useState(null);
  const [submittingCompletion, setSubmittingCompletion] = useState(false);
  const [businessRating, setBusinessRating] = useState(5);
  const [businessPayAmount, setBusinessPayAmount] = useState('');
  const [businessReviewOverall, setBusinessReviewOverall] = useState(5);
  const [businessReviewPaysOnTime, setBusinessReviewPaysOnTime] = useState(5);
  const [businessReviewWorkQuality, setBusinessReviewWorkQuality] = useState(5);
  const [businessReviewExperience, setBusinessReviewExperience] = useState(5);
  const [businessReviewText, setBusinessReviewText] = useState('');
  const [submittingBusinessReview, setSubmittingBusinessReview] = useState(false);
  const [businessReviewSubmitted, setBusinessReviewSubmitted] = useState(false);

  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
    }
  }, [currentUser, navigate]);

  useEffect(() => {
    const loadConversation = async () => {
      if (!currentUser || !conversationId) return;
      setLoading(true);
      setError('');
      try {
        const token = await currentUser.getIdToken();
        const res = await fetch(`${API_BASE_URL}/api/chat/conversation/${conversationId}`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.error || `Failed to load job`);
        }
        const json = await res.json();
        const convo = json.conversation || null;
        setConversation(convo);

        // If freelancer has already submitted a business review for this conversation,
        // keep the review form hidden (even after refresh) and show the saved message instead.
        if (convo && convo.freelancerReviewedBusiness) {
          setBusinessReviewSubmitted(true);
        }

        // Prefill pay amount from assignment if available
        const assignment = json.conversation?.assignment;
        if (assignment && assignment.pay_amount != null) {
          setBusinessPayAmount(String(assignment.pay_amount));
        }
      } catch (e) {
        console.error('Failed to load conversation/job', e);
        setError(e.message || 'Failed to load job');
      } finally {
        setLoading(false);
      }
    };

    loadConversation();
  }, [currentUser, conversationId]);

  const isFreelancer = currentRole === 'freelancer';
  const isBusiness = currentRole === 'business' || currentRole === 'employer';
  const status = conversation?.status || 'accepted';

  const jobPhase = useMemo(() => {
    if (!conversation) return null;
    const wpStatus = conversation.workProposal?.status;
    const completeF = !!conversation.completeFreelancer;
    const completeB = !!conversation.completeBusiness;

    if (status === 'pending') return 'PENDING_ACCEPTANCE';
    if (status === 'completed') return 'COMPLETED';
    if ((completeF || completeB) && !(completeF && completeB)) {
      return 'COMPLETION_REQUESTED';
    }
    if (wpStatus === 'confirmed') return 'CONFIRMED';
    if (wpStatus === 'proposed') return 'NEGOTIATING_SCHEDULE';
    return 'OPEN_CHAT';
  }, [conversation]);

  const phaseLabel = useMemo(() => {
    switch (jobPhase) {
      case 'PENDING_ACCEPTANCE':
        return t('jobPage.phasePending') || 'Waiting for confirmation';
      case 'NEGOTIATING_SCHEDULE':
        return t('jobPage.phaseNegotiating') || 'Negotiating schedule';
      case 'CONFIRMED':
        return t('jobPage.phaseConfirmed') || 'Job confirmed';
      case 'COMPLETION_REQUESTED':
        return t('jobPage.phaseCompletionRequested') || 'Completion requested';
      case 'COMPLETED':
        return t('jobPage.phaseCompleted') || 'Job completed';
      default:
        return t('jobPage.phaseOpenChat') || 'Active chat';
    }
  }, [jobPhase, t]);

  const handleAcceptConversation = async () => {
    if (!conversationId || !currentUser) return;
    try {
      const token = await currentUser.getIdToken();
      const res = await fetch(
        `${API_BASE_URL}/api/chat/conversation/${conversationId}/accept`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.error) {
        throw new Error(data.error || 'Failed to accept conversation');
      }
      setConversation(prev => prev ? { ...prev, status: 'accepted' } : prev);
    } catch (e) {
      console.error('Failed to accept conversation', e);
      setError(
        t('chat.failedAccept') ||
          'Failed to accept conversation. Please try again.'
      );
    }
  };

  const handleRejectConversation = async () => {
    if (!conversationId || !currentUser) return;
    if (!window.confirm(t('chat.sureReject'))) {
      return;
    }
    try {
      const token = await currentUser.getIdToken();
      const res = await fetch(
        `${API_BASE_URL}/api/chat/conversation/${conversationId}/reject`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.error) {
        throw new Error(data.error || 'Failed to reject conversation');
      }
      navigate(-1);
    } catch (e) {
      console.error('Failed to reject conversation', e);
      setError(
        t('chat.failedReject') ||
          'Failed to reject conversation. Please try again.'
      );
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch {
      // ignore
    }
  };

  const handleFreelancerMarkCompleted = async () => {
    if (!conversationId || !currentUser) return;
    setSubmittingCompletion(true);
    setError('');
    try {
      const token = await currentUser.getIdToken();
      const res = await fetch(
        `${API_BASE_URL}/api/chat/conversation/${conversationId}/request-complete`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({})
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.error) {
        throw new Error(data.error || 'Failed to request completion');
      }
      setConversation(prev => prev ? {
        ...prev,
        completeFreelancer: data.completeFreelancer ?? true,
        completeBusiness: data.completeBusiness ?? prev.completeBusiness,
        status: data.status || prev.status
      } : prev);
    } catch (e) {
      console.error('Failed to request completion as freelancer', e);
      setError(e.message || 'Failed to request completion');
    } finally {
      setSubmittingCompletion(false);
    }
  };

  const handleBusinessConfirmCompletion = async (e) => {
    e.preventDefault();
    if (!conversationId || !currentUser) return;

    if (businessRating && (isNaN(businessRating) || businessRating < 1 || businessRating > 5)) {
      setError(t('ratings.invalidRating') || 'Invalid rating. Please enter a number between 1 and 5.');
      return;
    }

    const normalizedAmount = String(businessPayAmount || '').replace(',', '').trim();
    const amount = Number(normalizedAmount);
    if (!normalizedAmount || Number.isNaN(amount) || amount <= 0) {
      setError(t('jobPage.invalidPayAmount') || 'Please enter a valid pay amount greater than 0.');
      return;
    }

    setSubmittingCompletion(true);
    setError('');
    try {
      const token = await currentUser.getIdToken();
      const payload = {
        payAmount: amount
      };
      if (!Number.isNaN(businessRating) && businessRating >= 1 && businessRating <= 5) {
        payload.rating = Math.round(businessRating);
      }

      const res = await fetch(
        `${API_BASE_URL}/api/chat/conversation/${conversationId}/request-complete`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.error) {
        throw new Error(data.error || 'Failed to confirm completion');
      }

      setConversation(prev => prev ? {
        ...prev,
        completeFreelancer: data.completeFreelancer ?? prev.completeFreelancer,
        completeBusiness: data.completeBusiness ?? true,
        status: data.status || prev.status
      } : prev);
    } catch (e) {
      console.error('Failed to confirm completion as business', e);
      setError(e.message || 'Failed to confirm completion');
    } finally {
      setSubmittingCompletion(false);
    }
  };

  const handleFreelancerSubmitBusinessReview = async (e) => {
    e.preventDefault();
    if (!conversationId || !currentUser || !conversation || !isFreelancer) return;

    const businessId = conversation.otherUser?.uid;
    if (!businessId) {
      setError(t('jobPage.businessNotFoundForReview') || 'Cannot determine business to review.');
      return;
    }

    const rOverall = businessReviewOverall;
    const rPays = businessReviewPaysOnTime;
    const rWork = businessReviewWorkQuality;
    const rExp = businessReviewExperience;

    const ratings = [rOverall, rPays, rWork, rExp];
    if (!ratings.every((r) => r && r >= 1 && r <= 5)) {
      setError(
        t('jobPage.invalidBusinessRating') ||
          'Please enter ratings between 1 and 5 for all fields.'
      );
      return;
    }

    setSubmittingBusinessReview(true);
    setError('');
    try {
      const token = await currentUser.getIdToken();
      const payload = {
        conversation_id: conversationId,
        job_id:
          conversation.jobId ||
          conversation.job?.id ||
          conversation.assignment?.job_id ||
          null,
        rating_overall: rOverall,
        rating_pays_on_time: rPays,
        rating_work_quality: rWork,
        rating_experience: rExp,
        review_text: businessReviewText || ''
      };

      const res = await fetch(
        `${API_BASE_URL}/api/business/${businessId}/reviews`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.error) {
        throw new Error(data.error || 'Failed to submit review');
      }

      setBusinessReviewSubmitted(true);
    } catch (e) {
      console.error('Failed to submit business review', e);
      setError(
        t('jobPage.failedToSubmitBusinessReview') ||
          'Failed to submit business review.'
      );
    } finally {
      setSubmittingBusinessReview(false);
    }
  };

  if (!currentUser) {
    return null;
  }

  return (
    <div
      className={`job-page-container ${isRTL ? 'rtl' : ''}`}
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      <header className="job-page-header">
        <div className="job-page-header-content">
          <div className="job-page-header-left">
            <button
              className="job-page-nav-button"
              onClick={() => navigate(-1)}
            >
              {t('common.back') || 'Back'}
            </button>
          </div>
          <div className="job-page-title">
            <h1>{t('jobPage.title') || 'Job details'}</h1>
            <p>{t('jobPage.subtitle') || 'Review the agreed work, schedule, and completion status.'}</p>
          </div>
          <div className="job-page-header-right">
            <LanguageSwitcher />
            <button
              className="job-page-nav-button"
              onClick={() =>
                navigate(
                  isBusiness
                    ? '/business/dashboard'
                    : '/freelancer/dashboard'
                )
              }
            >
              {t(
                isBusiness
                  ? 'businessDashboard.title'
                  : 'freelancerDashboard.title'
              ) || 'Dashboard'}
            </button>
            <button
              className="job-page-nav-button"
              onClick={handleLogout}
            >
              {t('dashboard.logout') || 'Logout'}
            </button>
          </div>
        </div>
      </header>

      <main className="job-page-main">
        {loading ? (
          <div className="job-page-loading">
            <div className="job-page-loading-spinner"></div>
            <p className="job-page-loading-text">{t('common.loading') || 'Loading...'}</p>
          </div>
        ) : error ? (
          <div className="job-page-error">{error}</div>
        ) : !conversation ? (
          <div className="job-page-error">
            {t('jobPage.notFound') || 'Job / conversation not found.'}
          </div>
        ) : (
          <>
            <section className="job-page-summary">
              <div className="job-page-summary-main">
                <h2 className="job-title">
                  {conversation.job?.title ||
                    conversation.assignment?.job_title ||
                    t('jobPage.untitledJob') ||
                    'Job with '}{" "}
                  {!conversation.job?.title && !conversation.assignment?.job_title &&
                    (conversation.otherUser?.displayName || conversation.otherUser?.email)}
                </h2>
                <p className="job-phase-label">{phaseLabel}</p>
                {status === 'pending' && isBusiness && (
                  <div className="job-page-summary-actions">
                    <button
                      className="job-page-primary-button"
                      onClick={handleAcceptConversation}
                    >
                      {t('chat.accept')}
                    </button>
                    <button
                      className="job-page-secondary-button"
                      onClick={handleRejectConversation}
                    >
                      {t('chat.reject')}
                    </button>
                  </div>
                )}
              </div>
              <div className="job-page-summary-meta">
                {conversation.otherUser && (
                  <div className="job-meta-item">
                    <span className="job-meta-label">
                      {isBusiness
                        ? (t('jobPage.freelancer') || 'Freelancer')
                        : (t('jobPage.business') || 'Business')}
                      :
                    </span>
                    <span className="job-meta-value">
                      {conversation.otherUser.displayName ||
                        conversation.otherUser.email}
                    </span>
                  </div>
                )}
                {isBusiness && conversation.otherUser?.uid && (
                  <div className="job-meta-item">
                    <button
                      className="job-page-secondary-button"
                      type="button"
                      onClick={() => navigate(`/freelancer/${conversation.otherUser.uid}/public`)}
                    >
                      {t('jobs.viewProfile') || 'View freelancer profile'}
                    </button>
                  </div>
                )}
                {isFreelancer && conversation.otherUser?.uid && (
                  <div className="job-meta-item">
                    <button
                      className="job-page-secondary-button"
                      type="button"
                      onClick={() => navigate(`/business/${conversation.otherUser.uid}/public`)}
                    >
                      {t('jobs.viewBusiness') || 'View business profile'}
                    </button>
                  </div>
                )}
                {conversation.assignment?.pay_amount != null && (
                  <div className="job-meta-item">
                    <span className="job-meta-label">
                      {t('freelancerEarnings.total') || 'Total earnings'}
                      :
                    </span>
                    <span className="job-meta-value highlight">
                      ₪{Number(conversation.assignment.pay_amount).toLocaleString()}
                    </span>
                  </div>
                )}
                {conversation.completedAt && (
                  <div className="job-meta-item">
                    <span className="job-meta-label">
                      {t('businessDashboard.completedDate') || 'Completed'}
                      :
                    </span>
                    <span className="job-meta-value">
                      {new Date(conversation.completedAt._seconds
                        ? conversation.completedAt._seconds * 1000
                        : conversation.completedAt).toLocaleString()}
                    </span>
                  </div>
                )}
              </div>
            </section>

            <section className="job-page-grid">
              <div className="job-page-card">
                <h3>{t('jobPage.workDetails') || 'Work details'}</h3>
                {conversation.job ? (
                  <ul className="job-details-list">
                    <li>
                      <span className="job-meta-label">
                        {t('jobs.jobDescription') || 'Description'}: <span> </span>
                      </span>
                      <span className="job-meta-value">
                        {conversation.job.description || t('jobs.jobDescriptionPlaceholder')}
                      </span>
                    </li>
                    {(conversation.job.start_date || conversation.job.end_date) && (
                      <li>
                        <span className="job-meta-label">
                          {t('jobs.startDate') || 'Dates'}: <span> </span>
                        </span>
                        <span className="job-meta-value">
                          {conversation.job.start_date}
                          {conversation.job.end_date && conversation.job.end_date !== conversation.job.start_date
                            ? ` - ${conversation.job.end_date}`
                            : ''}
                        </span>
                      </li>
                    )}
                    {(conversation.job.start_time || conversation.job.end_time) && (
                      <li>
                        <span className="job-meta-label">
                          {t('jobs.startTime') || 'Time'}: <span> </span>
                        </span>
                        <span className="job-meta-value">
                          {conversation.job.start_time}
                          {conversation.job.end_time && ` - ${conversation.job.end_time}`}
                        </span>
                      </li>
                    )}
                    {conversation.job.location && (
                      <li>
                        <span className="job-meta-label">
                          {t('jobs.location') || 'Location'}: <span> </span>
                        </span>
                        <span className="job-meta-value">
                          {conversation.job.location}
                        </span>
                      </li>
                    )}
                    {(conversation.job.hourly_rate || conversation.job.day_rate) && (
                      <li>
                        <span className="job-meta-label">
                          {t('jobs.pay') || 'Pay'}: <span> </span>
                        </span>
                        <span className="job-meta-value">
                          {conversation.job.hourly_rate
                            ? `₪${conversation.job.hourly_rate}/hr`
                            : ''}
                          {conversation.job.hourly_rate && conversation.job.day_rate ? ' · ' : ''}
                          {conversation.job.day_rate
                            ? `₪${conversation.job.day_rate}/day`
                            : ''}
                        </span>
                      </li>
                    )}
                    {conversation.job.expertise_level && (
                      <li>
                        <span className="job-meta-label">
                          {t('jobs.expertiseLevel') || 'Expertise Level'}: <span> </span>
                        </span>
                        <span className="job-meta-value">
                          {t(`jobs.expertiseLevel${conversation.job.expertise_level.charAt(0).toUpperCase() + conversation.job.expertise_level.slice(1)}`) ||
                            conversation.job.expertise_level.charAt(0).toUpperCase() + conversation.job.expertise_level.slice(1)}
                        </span>
                      </li>
                    )}
                    {conversation.job.mobile_required && (
                      <li>
                        <span className="job-meta-label">
                          {t('jobs.mobileRequired') || 'Mobile Required'}: <span> </span>
                        </span>
                        <span className="job-meta-value">
                          {t('common.yes') || 'Yes'}
                        </span>
                      </li>
                    )}
                    {conversation.job.equipment_required && (
                      <li>
                        <span className="job-meta-label">
                          {t('jobs.equipmentRequired') || 'Equipment Required'}: <span> </span>
                        </span>
                        <span className="job-meta-value">
                          {t('common.yes') || 'Yes'}
                        </span>
                      </li>
                    )}
                  </ul>
                ) : conversation.assignment ? (
                  <ul className="job-details-list">
                    <li>
                      <span className="job-meta-label">
                        {t('freelancerEarnings.assignment') || 'Assignment'}:
                      </span>
                      <span className="job-meta-value">
                        {conversation.assignment.job_title ||
                          conversation.assignment.job_id ||
                          t('freelancerEarnings.assignment')}
                      </span>
                    </li>
                    {conversation.assignment.start_date && (
                      <li>
                        <span className="job-meta-label">
                          {t('jobs.startDate') || 'Start date'}:
                        </span>
                        <span className="job-meta-value">
                          {conversation.assignment.start_date}
                        </span>
                      </li>
                    )}
                    {conversation.assignment.end_date && (
                      <li>
                        <span className="job-meta-label">
                          {t('jobs.endDate') || 'End date'}:
                        </span>
                        <span className="job-meta-value">
                          {conversation.assignment.end_date}
                        </span>
                      </li>
                    )}
                    {(conversation.assignment.start_time || conversation.assignment.end_time) && (
                      <li>
                        <span className="job-meta-label">
                          {t('jobs.startTime') || 'Time'}:
                        </span>
                        <span className="job-meta-value">
                          {conversation.assignment.start_time}
                          {conversation.assignment.end_time && ` - ${conversation.assignment.end_time}`}
                        </span>
                      </li>
                    )}
                  </ul>
                ) : (
                  <p className="job-page-empty">
                    {t('jobPage.noJobDetails') || 'No structured job details were saved for this conversation.'}
                  </p>
                )}
              </div>

              <div className="job-page-card">
                <h3>{t('jobPage.schedule') || 'Schedule'}</h3>
                {conversation.workProposal?.status ? (
                  <>
                    <p className="job-meta-value">
                      {(conversation.workProposal.status || '').toLowerCase() === 'confirmed'
                        ? t('jobPage.scheduleConfirmed') || 'Schedule confirmed by both sides.'
                        : conversation.workProposal.status === 'proposed'
                          ? t('jobPage.scheduleProposed') || 'Schedule proposed by freelancer, awaiting business response.'
                          : t('jobPage.scheduleStatus', { status: conversation.workProposal.status }) ||
                            `Schedule status: ${conversation.workProposal.status}`}
                    </p>
                    {Array.isArray(conversation.workProposal.slots) &&
                      conversation.workProposal.slots.length > 0 && (
                        <ul className="job-details-list">
                          {conversation.workProposal.slots.map((slot, idx) => (
                            <li key={idx}>
                              <span className="job-meta-label">
                                {slot.startDate}
                                {slot.startTime ? ` ${slot.startTime}` : ''}
                                {' - '}
                              </span>
                              <span className="job-meta-value">
                                {slot.endDate}
                                {slot.endTime ? ` ${slot.endTime}` : ''}
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                    {conversation.workProposal.notes && (
                      <p className="job-page-notes">
                        {conversation.workProposal.notes}
                      </p>
                    )}
                  </>
                ) : (
                  <p className="job-page-empty">
                    {t('jobPage.noSchedule') || 'No schedule has been proposed yet. Use the chat to agree on dates.'}
                  </p>
                )}
                {status === 'accepted' || status === 'completed' ? (
                  <button
                    className="job-page-secondary-button"
                    onClick={() =>
                      navigate(`/chat?conversationId=${conversationId}`)
                    }
                  >
                    {t('jobPage.openChat') || 'Open chat for this job'}
                  </button>
                ) : (
                  <p className="job-page-note">
                    {t('jobPage.openChatShort') ||
                      'Accept the chat first, then use the chat inbox for this job.'}
                  </p>
                )}
              </div>

              <div className="job-page-card">
                <h3>{t('jobPage.completion') || 'Completion & payment'}</h3>
                <p className="job-page-completion-status">
                  {conversation.completeFreelancer
                    ? t('jobPage.freelancerMarkedComplete') || 'Freelancer marked the job as completed.'
                    : t('jobPage.freelancerNotComplete') || 'Freelancer has not yet marked the job as completed.'}
                  {' '}
                  {conversation.completeBusiness
                    ? t('jobPage.businessMarkedComplete') || 'Business confirmed completion and payment.'
                    : t('jobPage.businessNotComplete') || 'Business has not yet confirmed completion.'}
                </p>

                {jobPhase === 'COMPLETED' && conversation.assignment && (
                  <div className="job-page-completion-summary">
                    <p>
                      {t('jobPage.completedSummary') ||
                        'This job is fully completed. Any later pay adjustments must be agreed by both sides.'}
                    </p>
                    <p>
                      <strong>
                        {t('freelancerEarnings.total') || 'Total earnings'}:
                      </strong>{' '}
                      ₪{Number(conversation.assignment.pay_amount || 0).toLocaleString()}
                    </p>
                  </div>
                )}

                {jobPhase !== 'COMPLETED' && (
                  <div className="job-page-completion-actions">
                    {isFreelancer && !conversation.completeFreelancer && (
                      <button
                        className="job-page-primary-button"
                        onClick={handleFreelancerMarkCompleted}
                        disabled={submittingCompletion}
                      >
                        {submittingCompletion
                          ? (t('common.saving') || 'Saving...')
                          : (t('chat.markCompleted') || 'Mark job as completed')}
                      </button>
                    )}

                    {isFreelancer &&
                      conversation.completeFreelancer &&
                      conversation.otherUser && (
                        <form
                          onSubmit={handleFreelancerSubmitBusinessReview}
                          className="job-page-business-form"
                        >
                          <h4>
                            {t('jobPage.rateBusinessTitle') ||
                              'Rate this business'}
                          </h4>
                          <p className="job-page-note">
                            {t('jobPage.rateBusinessHint') ||
                              'Your rating will appear on the business public profile and helps other freelancers.'}
                          </p>
                          {businessReviewSubmitted ? (
                            <p className="job-page-success">
                              {t('jobPage.businessReviewSubmitted') ||
                                'Your review has been saved.'}
                            </p>
                          ) : (
                            <>
                              <div className="job-form-row">
                                <StarRating
                                  value={businessReviewOverall}
                                  onChange={setBusinessReviewOverall}
                                  label={t('businessPublic.ratingOverall') || 'Overall'}
                                  size={28}
                                />
                              </div>
                              <div className="job-form-row">
                                <StarRating
                                  value={businessReviewPaysOnTime}
                                  onChange={setBusinessReviewPaysOnTime}
                                  label={t('businessPublic.ratingPaysOnTime') || 'Pays on time'}
                                  size={28}
                                />
                              </div>
                              <div className="job-form-row">
                                <StarRating
                                  value={businessReviewWorkQuality}
                                  onChange={setBusinessReviewWorkQuality}
                                  label={t('businessPublic.ratingWorkQuality') || 'Work quality'}
                                  size={28}
                                />
                              </div>
                              <div className="job-form-row">
                                <StarRating
                                  value={businessReviewExperience}
                                  onChange={setBusinessReviewExperience}
                                  label={t('businessPublic.ratingExperience') || 'Experience'}
                                  size={28}
                                />
                              </div>
                              <div className="job-form-row">
                                <label>
                                  {t('jobPage.businessReviewTextLabel') ||
                                    'Short review (optional)'}
                                  <textarea
                                    rows={3}
                                    value={businessReviewText}
                                    onChange={(e) =>
                                      setBusinessReviewText(e.target.value)
                                    }
                                  />
                                </label>
                              </div>
                              <button
                                type="submit"
                                className="job-page-primary-button"
                                disabled={submittingBusinessReview}
                              >
                                {submittingBusinessReview
                                  ? t('common.saving') || 'Saving...'
                                  : t('jobPage.submitBusinessReview') ||
                                    'Submit review'}
                              </button>
                            </>
                          )}
                        </form>
                      )}

                    {isBusiness && conversation.completeFreelancer && !conversation.completeBusiness && (
                      <form onSubmit={handleBusinessConfirmCompletion} className="job-page-business-form">
                        <div className="job-form-row">
                          <label>
                            {t('jobPage.businessPayAmountLabel') || 'How much did you pay the freelancer? (₪)'}
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={businessPayAmount}
                              onChange={(e) => setBusinessPayAmount(e.target.value)}
                            />
                          </label>
                        </div>
                        <div className="job-form-row">
                          <StarRating
                            value={businessRating}
                            onChange={setBusinessRating}
                            label={t('ratings.enterRating') || 'Rate this freelancer'}
                            size={28}
                          />
                        </div>
                        <button
                          type="submit"
                          className="job-page-primary-button"
                          disabled={submittingCompletion}
                        >
                          {submittingCompletion
                            ? (t('common.saving') || 'Saving...')
                            : (t('chat.confirmCompletion') || 'Confirm completion & payment')}
                        </button>
                      </form>
                    )}

                    {!isFreelancer && !isBusiness && (
                      <p className="job-page-empty">
                        {t('jobPage.unknownRole') || 'Your role is not recognized for completion. Please contact support.'}
                      </p>
                    )}
                  </div>
                )}

                {isBusiness && conversation.assignment && (
                  <p className="job-page-note">
                    {t('jobPage.payChangeHint') ||
                      'If you need to adjust the pay afterwards, you can use the “Completed jobs” section in your business dashboard. Both parties must agree to any change.'}
                  </p>
                )}
                <br>
                </br>
                <br>
                </br>
              </div>
            </section>
          </>
        )}
      </main>
    </div>
  );
}


